import { useCallback, useEffect, useMemo, useRef, useState } from "react"

import useWorldStore from "../store/worldStore"
import ChatAppEmbedFrame from "./ChatAppEmbedFrame"
import {
  getChatAppConnectionIndicator,
  getDefaultChatAppConnectionStatus,
} from "../lib/chatAppEmbed"
import { fetchWorldBootstrap } from "../../../lib/backend/world"
import useFloatingWindowLayer from "../hooks/useFloatingWindowLayer"
import {
  shouldSkipPointerDragStart,
  startPointerSession,
} from "../../../lib/pointer-drag"

const IC = {
  message: "💬",
  expand: "⛶",
  shrink: "⊡",
  dash: "−",
  x: "×",
}

function ConnectionBadge({ indicator }) {
  return (
    <div
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        padding: "3px 8px",
        borderRadius: 999,
        background: "rgba(255,255,255,0.08)",
        border: "1px solid rgba(255,255,255,0.08)",
      }}
    >
      <span
        aria-hidden
        style={{
          width: 8,
          height: 8,
          borderRadius: "50%",
          background: indicator.color,
          boxShadow: `0 0 12px ${indicator.color}`,
          flexShrink: 0,
        }}
      />
      <span
        style={{
          fontFamily: "'Inter', ui-sans-serif, system-ui, sans-serif",
          fontSize: 10,
          color: "rgba(255,255,255,0.82)",
          letterSpacing: "0.03em",
        }}
      >
        {indicator.label}
      </span>
    </div>
  )
}

export default function ChatEmbedWindow() {
  const chatEmbeds = useWorldStore((s) => s.activeChatEmbeds || [])
  const agents = useWorldStore((s) => s.agents)
  const openChat = useWorldStore((s) => s.openChat)

  useEffect(() => {
    const numpadMap = {
      Numpad1: agents[0]?.id,
      Numpad2: agents[1]?.id,
      Numpad3: agents[2]?.id,
    }

    const handler = (e) => {
      const agentId = numpadMap[e.code]
      if (!agentId) return
      if (e.target.tagName === "INPUT" || e.target.tagName === "TEXTAREA")
        return

      openChat(agentId)
    }

    window.addEventListener("keydown", handler)
    return () => window.removeEventListener("keydown", handler)
  }, [agents, openChat])

  return chatEmbeds.map((chatEmbed, index) => (
    <ChatEmbedWindowFrame
      key={chatEmbed.agentId || `chat-${index}`}
      agents={agents}
      chatEmbed={chatEmbed}
    />
  ))
}

function ChatEmbedWindowFrame({ chatEmbed, agents }) {
  const closeChat = useWorldStore((s) => s.closeChatEmbed)
  const toggleMin = useWorldStore((s) => s.toggleChatEmbedMinimized)
  const toggleFs = useWorldStore((s) => s.toggleChatEmbedFullscreen)
  const setPos = useWorldStore((s) => s.setChatEmbedPosition)
  const setSize = useWorldStore((s) => s.setChatEmbedSize)
  const windowId = chatEmbed ? `chat-embed:${chatEmbed.agentId}` : null
  const {
    zIndex: layeredZIndex,
    focusProps,
    focusWindow,
  } = useFloatingWindowLayer(windowId)

  const dragRef = useRef(null)
  const repairAttemptedAgentIdRef = useRef(null)
  const closeTimeoutRef = useRef(null)
  const [repairingBinding, setRepairingBinding] = useState(false)
  const [closingAgentId, setClosingAgentId] = useState(null)

  useEffect(() => {
    return () => {
      if (closeTimeoutRef.current) {
        window.clearTimeout(closeTimeoutRef.current)
        closeTimeoutRef.current = null
      }
    }
  }, [])

  const activeAgent = useMemo(() => {
    if (!chatEmbed) return null
    return agents.find((agent) => agent.id === chatEmbed.agentId) ?? null
  }, [agents, chatEmbed])

  const onDragStart = useCallback(
    (e) => {
      if (!chatEmbed || chatEmbed.fullscreen || shouldSkipPointerDragStart(e))
        return
      focusWindow()

      const startX = e.clientX
      const startY = e.clientY
      const startPos = chatEmbed.minimized
        ? (dragRef.current ?? { x: 16, y: window.innerHeight - 176 })
        : chatEmbed.position
      dragRef.current = startPos

      startPointerSession(e, {
        onMove: (ev) => {
          const nx = Math.max(
            0,
            Math.min(window.innerWidth - 160, startPos.x + ev.clientX - startX)
          )
          const ny = Math.max(
            0,
            Math.min(window.innerHeight - 60, startPos.y + ev.clientY - startY)
          )
          setPos(chatEmbed.agentId, nx, ny)
        },
      })
    },
    [chatEmbed, focusWindow, setPos]
  )

  const onResizeStart = useCallback(
    (e) => {
      if (!chatEmbed || chatEmbed.fullscreen || chatEmbed.minimized) return
      e.stopPropagation()

      const startX = e.clientX
      const startY = e.clientY
      const startW = chatEmbed.size.w
      const startH = chatEmbed.size.h

      startPointerSession(e, {
        onMove: (ev) => {
          const nw = Math.max(
            560,
            Math.min(window.innerWidth, startW + ev.clientX - startX)
          )
          const nh = Math.max(
            380,
            Math.min(window.innerHeight, startH + ev.clientY - startY)
          )
          setSize(chatEmbed.agentId, nw, nh)
        },
      })
    },
    [chatEmbed, setSize]
  )

  const handleCloseChat = useCallback(() => {
    if (!chatEmbed || closingAgentId === activeAgent?.id) return

    if (!activeAgent?.chatAppCharacterId) {
      closeChat(chatEmbed.agentId)
      return
    }

    setClosingAgentId(chatEmbed.agentId)
    closeTimeoutRef.current = window.setTimeout(() => {
      closeTimeoutRef.current = null
      closeChat(chatEmbed.agentId)
    }, 200)
  }, [activeAgent, chatEmbed, closeChat, closingAgentId])

  const showRepairingBinding = Boolean(
    chatEmbed &&
    activeAgent?.id &&
    !activeAgent.chatAppCharacterId &&
    repairingBinding
  )
  const [connectionStatus, setConnectionStatus] = useState(() =>
    getDefaultChatAppConnectionStatus()
  )
  const connectionIndicator = useMemo(
    () => getChatAppConnectionIndicator(connectionStatus),
    [connectionStatus]
  )

  useEffect(() => {
    setConnectionStatus(getDefaultChatAppConnectionStatus())
  }, [chatEmbed?.agentId])

  useEffect(() => {
    if (!chatEmbed) {
      repairAttemptedAgentIdRef.current = null
      return
    }

    if (!activeAgent?.id) {
      return
    }

    if (activeAgent.chatAppCharacterId) {
      repairAttemptedAgentIdRef.current = null
      return
    }

    if (repairAttemptedAgentIdRef.current === activeAgent.id) {
      return
    }

    repairAttemptedAgentIdRef.current = activeAgent.id
    let cancelled = false
    const startRepairFrame = requestAnimationFrame(() => {
      setRepairingBinding(true)
    })

    void fetchWorldBootstrap()
      .then((bootstrap) => {
        if (cancelled) return
        useWorldStore.getState().applyBootstrap(bootstrap)
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) {
          setRepairingBinding(false)
        }
      })

    return () => {
      cancelled = true
      cancelAnimationFrame(startRepairFrame)
    }
  }, [activeAgent?.chatAppCharacterId, activeAgent?.id, chatEmbed])

  if (!chatEmbed) return null

  const pos = chatEmbed.fullscreen ? { x: 0, y: 0 } : chatEmbed.position
  const size = chatEmbed.fullscreen
    ? { w: window.innerWidth, h: window.innerHeight }
    : chatEmbed.size

  return (
    <>
      <div
        aria-hidden={chatEmbed.minimized}
        {...focusProps}
        style={{
          position: "fixed",
          left: chatEmbed.minimized ? -10000 : pos.x,
          top: chatEmbed.minimized ? 0 : pos.y,
          width: size.w,
          height: size.h,
          background: "rgba(8,10,16,0.97)",
          border: chatEmbed.fullscreen
            ? "none"
            : "1px solid rgba(255,255,255,0.15)",
          borderRadius: chatEmbed.fullscreen ? 0 : 12,
          zIndex: layeredZIndex,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          boxShadow: "0 30px 80px rgba(0,0,0,0.45)",
          opacity: chatEmbed.minimized ? 0 : 1,
          pointerEvents: chatEmbed.minimized ? "none" : "auto",
        }}
      >
        <div
          onPointerDown={onDragStart}
          style={{
            height: 40,
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            padding: "0 12px",
            gap: 10,
            cursor: chatEmbed.fullscreen ? "default" : "grab",
            userSelect: "none",
            borderBottom: "1px solid rgba(255,255,255,0.08)",
            background: "rgba(0,0,0,0.18)",
            touchAction: "none",
          }}
        >
          <span style={{ fontSize: 14 }}>{IC.message}</span>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div
              style={{
                fontFamily: "'Inter', ui-sans-serif, system-ui, sans-serif",
                fontSize: 10,
                color: "rgba(255,255,255,0.42)",
                textTransform: "uppercase",
                letterSpacing: "0.08em",
              }}
            >
              Chat App
            </div>
            <div
              style={{
                fontFamily: "'Inter', ui-sans-serif, system-ui, sans-serif",
                fontSize: 11,
                color: "white",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {activeAgent
                ? `Chatting with ${activeAgent.characterConfig?.name || activeAgent.name}`
                : "Chat unavailable"}
            </div>
          </div>
          <ConnectionBadge indicator={connectionIndicator} />
          <button
            onClick={() => toggleFs(chatEmbed.agentId)}
            style={headerBtn}
            title="Fullscreen"
          >
            {chatEmbed.fullscreen ? IC.shrink : IC.expand}
          </button>
          <button
            onClick={() => toggleMin(chatEmbed.agentId)}
            style={headerBtn}
            title="Minimize"
          >
            {IC.dash}
          </button>
          <button
            onClick={handleCloseChat}
            style={headerBtn}
            title="Close chat"
          >
            {IC.x}
          </button>
        </div>

        {showRepairingBinding ? (
          <div
            style={{
              flexShrink: 0,
              padding: "10px 14px",
              background: "rgba(245, 158, 11, 0.16)",
              borderBottom: "1px solid rgba(245, 158, 11, 0.26)",
              color: "rgba(255, 235, 204, 0.96)",
              fontFamily: "'Inter', ui-sans-serif, system-ui, sans-serif",
              fontSize: 11,
              letterSpacing: "0.02em",
            }}
          >
            Refreshing world agent binding before launching chat…
          </div>
        ) : null}

        <ChatAppEmbedFrame
          agent={activeAgent}
          title="Chat App"
          uiMode="text"
          onConnectionStatusChange={setConnectionStatus}
        />

        {!chatEmbed.fullscreen && !chatEmbed.minimized ? (
          <div
            onPointerDown={onResizeStart}
            style={{
              position: "absolute",
              bottom: 0,
              right: 0,
              width: 18,
              height: 18,
              cursor: "nwse-resize",
              background:
                "linear-gradient(135deg, transparent 0 50%, rgba(255,255,255,0.2) 50% 60%, transparent 60% 100%)",
              touchAction: "none",
            }}
          />
        ) : null}
      </div>

      {chatEmbed.minimized ? (
        <div
          onPointerDown={onDragStart}
          {...focusProps}
          style={{
            position: "fixed",
            left: chatEmbed.position.x,
            top: chatEmbed.position.y,
            width: 280,
            height: 76,
            borderRadius: 14,
            border: "1px solid rgba(255,255,255,0.16)",
            overflow: "hidden",
            zIndex: layeredZIndex,
            cursor: "grab",
            background: "rgba(8,10,16,0.96)",
            display: "flex",
            alignItems: "center",
            padding: "0 14px",
            gap: 12,
            touchAction: "none",
          }}
        >
          <div
            style={{
              width: 36,
              height: 36,
              borderRadius: "50%",
              background: activeAgent?.color ?? "rgba(255,255,255,0.14)",
              boxShadow: activeAgent
                ? `0 0 20px ${activeAgent.color}55`
                : "none",
              flexShrink: 0,
            }}
          />
          <div style={{ minWidth: 0, flex: 1 }}>
            <div
              style={{
                fontFamily: "'Inter', ui-sans-serif, system-ui, sans-serif",
                fontSize: 10,
                color: "rgba(255,255,255,0.45)",
                textTransform: "uppercase",
                letterSpacing: "0.08em",
              }}
            >
              Chat App
            </div>
            <div
              style={{
                fontFamily: "'Inter', ui-sans-serif, system-ui, sans-serif",
                fontSize: 12,
                color: "white",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {activeAgent?.characterConfig?.name ||
                activeAgent?.name ||
                "Unknown agent"}
            </div>
          </div>
          <ConnectionBadge indicator={connectionIndicator} />
          <button
            onClick={(e) => {
              e.stopPropagation()
              toggleMin(chatEmbed.agentId)
            }}
            style={miniBtn}
            title="Expand"
          >
            {IC.expand}
          </button>
        </div>
      ) : null}
    </>
  )
}

const headerBtn = {
  background: "none",
  border: "none",
  color: "rgba(255,255,255,0.5)",
  fontSize: 14,
  cursor: "pointer",
  padding: "0 4px",
  fontFamily: "'Inter', ui-sans-serif, system-ui, sans-serif",
}

const miniBtn = {
  width: 28,
  height: 28,
  borderRadius: 8,
  background: "rgba(255,255,255,0.08)",
  border: "none",
  color: "white",
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
}
